"use client";
import { Box, Typography } from "@mui/material";
import { TrendingUp } from "@mui/icons-material";
import React from "react";

interface ClientGrowthSlideProps {
  data: {
    startCount: number;
    endCount: number;
  };
}

export default function ClientGrowthSlide({ data }: ClientGrowthSlideProps) {
  const growth = data.endCount - data.startCount;
  const growthPercent =
    data.startCount === 0
      ? data.endCount > 0
        ? 100
        : 0
      : Math.round((growth / data.startCount) * 100);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a0a0a",
        position: "relative",
        color: "white",
        p: 4,
        textAlign: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "60%",
          background:
            "linear-gradient(to top, rgba(6, 182, 212, 0.2), transparent)",
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <TrendingUp
          sx={{
            fontSize: 60,
            mb: 3,
            color: "#06b6d4",
            animation: "bounce 1s ease-in-out infinite",
            "@keyframes bounce": {
              "0%, 100%": {
                transform: "translateY(0) rotate(0deg)",
              },
              "50%": {
                transform: "translateY(-10px) rotate(5deg)",
              },
            },
          }}
        />
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: 4,
            animation: "fadeIn 0.5s ease-out",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
              },
              "100%": {
                opacity: 1,
              },
            },
          }}
        >
          Growing Your Business
        </Typography>
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            opacity: 0.8,
            animation: "fadeIn 0.5s ease-out 0.2s both",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
              },
              "100%": {
                opacity: 1,
              },
            },
          }}
        >
          You went from
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            mb: 2,
            width: "100%",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              animation:
                "scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.4s both",
              "@keyframes scaleIn": {
                "0%": {
                  transform: "scale(0)",
                  opacity: 0,
                },
                "100%": {
                  transform: "scale(1)",
                  opacity: 1,
                },
              },
            }}
          >
            {data.startCount}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              color: "#06b6d4",
              animation: "fadeIn 0.3s ease-out 0.6s both",
              "@keyframes fadeIn": {
                "0%": {
                  opacity: 0,
                },
                "100%": {
                  opacity: 1,
                },
              },
            }}
          >
            →
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              animation:
                "scaleIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.7s both",
            }}
          >
            {data.endCount}
          </Typography>
        </Box>
        <Typography
          variant="h6"
          sx={{
            opacity: 0.8,
            animation: "fadeIn 0.5s ease-out 0.9s both",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
              },
              "100%": {
                opacity: 1,
              },
            },
          }}
        >
          clients
        </Typography>
        <Box
          sx={{
            mt: 4,
            px: 3,
            py: 1.5,
            bgcolor: "rgba(6, 182, 212, 0.2)",
            border: "2px solid #06b6d4",
            borderRadius: 3,
            animation:
              "popIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 1.1s both",
            "@keyframes popIn": {
              "0%": {
                transform: "scale(0) rotate(-10deg)",
                opacity: 0,
              },
              "100%": {
                transform: "scale(1) rotate(0deg)",
                opacity: 1,
              },
            },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#06b6d4" }}>
            +{growthPercent}% growth!
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
